import type { TableCopyMethod } from '@/analytics/enums/table.enums'
import type { ColumnTag, TableEditResult } from '../schemas'
import { WarningTriangle } from '@/assets/icons'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import TableMatchText from './TableMatchText'
import CellEvidence from './CellEvidence'
import CellContentEditor from './CellContentEditor'
import { createCellEditorState, cellEditorContent, cellEditorNeedsVerification } from '../editor/content'
import { useRef, useState } from 'react'
import { ColumnKind, type TableCell, type CellSegment } from '../schemas'
import type { TableCellDraft } from '../hooks/useTableCellDrafts'
import TableCellEditMenu from './TableCellEditMenu'
import TagCellEditor from './TagCellEditor'

/** Text and fixed-choice editors share draft persistence and conflict handling. */
export default function EditableCell({
  draft,
  onDraftChange,
  cell,
  label,
  canEdit,
  onCopy,
  allowFootnotes = false,
  hideActions = false,
  selected = false,
  saving,
  onSave,
  matterId,
  tableId,
  pending = false,
  questionRevision,
  tags,
  allowMultiple = false,
  canCreateTags = false,
}: {
  draft: TableCellDraft | null
  onDraftChange: (draft: TableCellDraft | null) => void
  matterId?: string
  tableId?: string
  pending?: boolean
  questionRevision?: number
  tags?: ColumnTag[]
  allowMultiple?: boolean
  canCreateTags?: boolean
  cell: TableCell | undefined
  allowFootnotes?: boolean
  label: string
  onCopy?: (footnotes: boolean, method?: TableCopyMethod) => Promise<void>
  hideActions?: boolean
  selected?: boolean
  canEdit: boolean
  saving: boolean
  onSave: (
    value: string | string[],
    expectedValue: string,
    content: CellSegment[],
    expectedRevision?: string | null,
    clearVerification?: boolean,
    expectedQuestionRevision?: number,
    newTags?: ColumnTag[]
  ) => Promise<TableEditResult>
}) {
  const value = cell?.value ?? ''
  const notFound = Boolean(!tags && cell && cell.value === '' && cell.original_value !== null && !cell.edited_at && cell.segments.length === 0)
  // Only a user opening the editor should take focus; restoring a virtual row must not.
  const [focusEditor, setFocusEditor] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const saveInFlight = useRef(false)
  const references = cell?.references ?? []
  const startEdit = () => {
    if (!canEdit || saving) return
    const cellElement = container.current?.closest('td')
    const table = cellElement?.closest('table')
    const viewport = table?.parentElement
    if (cellElement && table?.tHead && viewport) {
      const hiddenHeight = table.tHead.getBoundingClientRect().bottom - cellElement.getBoundingClientRect().top
      if (hiddenHeight > 0) viewport.scrollTop -= hiddenHeight
    }
    setFocusEditor(true)
    const segments = cell?.segments ?? []
    const ids = [...new Set(segments.flatMap((segment) => segment.reference_ids))]
    onDraftChange({
      ...(tags
        ? { kind: ColumnKind.Tags, content: segments, needsVerification: Boolean(cell?.needs_verification) }
        : { kind: ColumnKind.Text, editorState: createCellEditorState(segments, cell?.needs_verification) }),
      references: [...references].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)),
      expectedValue: value,
      expectedRevision: cell?.revision,
      expectedQuestionRevision: questionRevision,
    })
  }
  const save = async () => {
    if (draft === null || saveInFlight.current) return
    saveInFlight.current = true
    const content = draft.kind === ColumnKind.Tags ? draft.content : cellEditorContent(draft.editorState.doc)
    const needsVerification = draft.kind === ColumnKind.Tags ? draft.needsVerification : cellEditorNeedsVerification(draft.editorState.doc)
    try {
      const saved = await onSave(
        draft.kind === ColumnKind.Tags ? content.map((segment) => segment.text).filter(Boolean) : content.map((segment) => segment.text).join(''),
        draft.expectedValue,
        content,
        draft.expectedRevision,
        Boolean(cell?.needs_verification && !needsVerification),
        draft.expectedQuestionRevision,
        draft.kind === ColumnKind.Tags ? draft.newTags?.filter((tag) => content.some((segment) => segment.text === tag.label)) : undefined
      )
      if (saved.ok || saved.conflict) onDraftChange(null)
    } finally {
      saveInFlight.current = false
    }
  }
  if (pending) {
    return (
      <div className="px-4 py-3 text-brand-neutral-400" title="Awaiting analysis" aria-label={`${label}: awaiting analysis`}>
        …
      </div>
    )
  }
  return (
    <div ref={container} className="min-w-0 px-4 py-3">
      {draft === null ? (
        <TableCellEditMenu
          allowFootnotes={allowFootnotes && Boolean(cell?.segments.some((segment) => segment.reference_ids.length > 0))}
          selected={selected}
          onCopy={onCopy}
          hideActions={hideActions}
          enabled={canEdit && !saving}
          onEdit={startEdit}
        >
          <div
            data-table-copy-content
            role={canEdit ? 'group' : undefined}
            tabIndex={canEdit && !saving ? 0 : undefined}
            aria-label={canEdit ? `Edit value for ${label}` : undefined}
            aria-description={canEdit ? 'Press Enter or Space to edit. Footnote links can be opened separately.' : undefined}
            aria-disabled={canEdit ? saving : undefined}
            onKeyDown={(event) => {
              if (!canEdit || saving || event.target !== event.currentTarget) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                startEdit()
              }
            }}
            className={`min-h-8 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 ${
              canEdit && !saving ? 'cursor-text' : ''
            }`}
          >
            <div>
              {notFound || (!value.trim() && !references.length) ? (
                <span
                  className="text-brand-neutral-500"
                  title={tags ? 'No tag selected' : notFound ? 'Not found' : 'Empty'}
                  aria-label={tags ? 'No tag selected' : notFound ? 'Not found' : 'Empty'}
                >
                  —
                </span>
              ) : tags ? (
                <CellEvidence segments={cell?.segments ?? []} references={references} matterId={matterId} tableId={tableId} tags={tags} />
              ) : references.length > 0 && matterId ? (
                <CellEvidence
                  tableId={tableId}
                  segments={cell?.segments ?? []}
                  references={references}
                  matterId={matterId}
                  highlightOnHover={canEdit && !saving}
                />
              ) : (
                <span className={`whitespace-pre-wrap break-words rounded ${canEdit && !saving ? 'hover:bg-brand-neutral-100' : ''}`} title={value}>
                  <TableMatchText text={value} />
                </span>
              )}
              {cell?.needs_verification && (
                <span data-table-copy-ignore className="inline-flex align-baseline mx-0.5">
                  <Tooltip title="We couldn’t confirm a supporting passage. Check this answer against the document.">
                    <span
                      tabIndex={0}
                      role="img"
                      aria-label="Needs manual verification"
                      className="inline-flex items-center min-h-[18px] px-1 pt-0 pb-[1px] rounded border text-xs text-brand-neutral-500 bg-brand-neutral-200 border-brand-neutral-300"
                    >
                      <WarningTriangle className="size-3.5" aria-hidden="true" />
                    </span>
                  </Tooltip>
                </span>
              )}
            </div>
          </div>
        </TableCellEditMenu>
      ) : draft.kind === ColumnKind.Tags ? (
        <TagCellEditor
          matterId={matterId}
          tags={tags ?? []}
          allowMultiple={allowMultiple}
          autoFocus={focusEditor}
          draft={draft}
          onDraftChange={onDraftChange}
          label={label}
          saving={saving}
          canCreate={canCreateTags}
          onSave={() => void save()}
          onCancel={() => {
            if (!saveInFlight.current) onDraftChange(null)
          }}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <CellContentEditor
            draft={draft}
            onDraftChange={onDraftChange}
            label={label}
            matterId={matterId}
            tableId={tableId}
            saving={saving}
            autoFocus={focusEditor}
            onSave={() => void save()}
            onCancel={() => {
              if (!saveInFlight.current) onDraftChange(null)
            }}
          />
        </div>
      )}
    </div>
  )
}
